/**
 * Domain model on top of the generic XML tree: which mission file is which,
 * and where the map-editable coordinates live inside each one.
 */
import {
  cloneDocumentPreservingIds,
  element,
  elementText,
  getAttr,
  getNumAttr,
  nextId,
  rootElement,
  setAttr,
  setElementText,
  textElement,
  walkElements,
  type XDocument,
  type XElement,
} from './xml';

export type DocKind =
  | 'territories' // env/<animal>_territories.xml - bear, wolf, zombies, ...
  | 'eventspawns' // cfgeventspawns.xml - where events (loot, vehicles, crashes) spawn
  | 'events' // events.xml - how many of each event, and what loot they carry
  | 'playerspawns' // cfgplayerspawnpoints.xml
  | 'types' // types.xml - loot economy, usage/tier tags decide which zones an item uses
  | 'unknown';

export type DocMeta = { kind: DocKind; label: string; mapEditable: boolean };

const KIND_BY_ROOT: Record<string, DocKind> = {
  territories: 'territories',
  'territory-type': 'territories',
  eventposdef: 'eventspawns',
  events: 'events',
  playerspawnpoints: 'playerspawns',
  types: 'types',
};

export const KIND_META: Record<DocKind, DocMeta> = {
  territories: { kind: 'territories', label: 'Animal / infected territories', mapEditable: true },
  eventspawns: { kind: 'eventspawns', label: 'Event spawn positions', mapEditable: true },
  events: { kind: 'events', label: 'Event economy', mapEditable: false },
  playerspawns: { kind: 'playerspawns', label: 'Player spawn points', mapEditable: true },
  types: { kind: 'types', label: 'Loot economy (types)', mapEditable: false },
  unknown: { kind: 'unknown', label: 'Unrecognised XML', mapEditable: true },
};

export function detectKind(doc: XDocument): DocKind {
  const root = rootElement(doc);
  if (!root) return 'unknown';
  return KIND_BY_ROOT[root.tag] ?? 'unknown';
}

/** A file loaded into the editor. */
export type EditorDoc = {
  id: string;
  fileName: string;
  kind: DocKind;
  doc: XDocument;
  visible: boolean;
  color: string;
  locked: boolean;
};

/**
 * A placed coordinate. `zone` elements (territories) carry a radius, `pos`
 * elements (event/player spawns) are single points with an optional yaw.
 */
export type Feature = {
  /** Id of the underlying XML element. */
  id: string;
  docId: string;
  /** Id of the owning `<territory>` / `<event>` / `<generator>` element. */
  groupId: string;
  tag: string;
  x: number;
  z: number;
  r?: number;
  a?: number;
  name?: string;
};

/** A `<territory>`, `<event>` or `<generator>` element: the container for features. */
export type FeatureGroup = {
  id: string;
  docId: string;
  tag: string;
  label: string;
  color?: string;
  count: number;
};

const POINT_TAGS = new Set(['zone', 'pos', 'position']);

export function isPointElement(el: XElement): boolean {
  return POINT_TAGS.has(el.tag) && getNumAttr(el, 'x') !== undefined && getNumAttr(el, 'z') !== undefined;
}

export function groupLabel(el: XElement, index: number): string {
  const name = getAttr(el, 'name');
  if (name) return name;
  // `<territory>` elements are anonymous - number them so they can be told apart.
  return `${el.tag.charAt(0).toUpperCase()}${el.tag.slice(1)} ${index + 1}`;
}

/** `0xAARRGGBB` (or a decimal colour) as `#rrggbb`, for `<input type="color">`. */
export function dayzColorToHex(raw: string | undefined): string {
  const css = dayzColorToCss(raw, '#3b9eff');
  const match = /rgb\((\d+), (\d+), (\d+)\)/.exec(css);
  if (!match) return css;
  return `#${[1, 2, 3].map((i) => Number(match[i]).toString(16).padStart(2, '0')).join('')}`;
}

export function hexToDayzColor(hex: string): string {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return `0xFF${value.toString(16).toUpperCase().padStart(6, '0')}`;
}

/** Extracts every map-editable coordinate in a document, with its owning group. */
export function extractFeatures(doc: EditorDoc): { features: Feature[]; groups: FeatureGroup[] } {
  const features: Feature[] = [];
  const groups = new Map<string, FeatureGroup>();
  const root = rootElement(doc.doc);

  for (const { el, parent } of walkElements(doc.doc)) {
    if (!isPointElement(el)) continue;
    const owner = parent && parent !== root ? parent : (root ?? parent);
    const groupId = owner ? owner.id : `${doc.id}:root`;
    if (owner && !groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        docId: doc.id,
        tag: owner.tag,
        label: groupLabel(owner, ownerIndex(root, owner)),
        color: getAttr(owner, 'color'),
        count: 0,
      });
    }
    const group = groups.get(groupId);
    if (group) group.count += 1;
    features.push({
      id: el.id,
      docId: doc.id,
      groupId,
      tag: el.tag,
      x: getNumAttr(el, 'x') ?? 0,
      z: getNumAttr(el, 'z') ?? 0,
      r: getNumAttr(el, 'r'),
      a: getNumAttr(el, 'a'),
      name: getAttr(el, 'name'),
    });
  }

  return { features, groups: [...groups.values()] };
}

/** Position of a group among its siblings, used to number anonymous groups. */
function ownerIndex(root: XElement | null, owner: XElement): number {
  if (!root) return 0;
  const siblings = root.children.filter((c): c is XElement => c.kind === 'element' && c.tag === owner.tag);
  return Math.max(siblings.indexOf(owner), 0);
}

/** Every group in a document, including ones that have no coordinates yet. */
export function listGroups(doc: EditorDoc): FeatureGroup[] {
  const root = rootElement(doc.doc);
  if (!root) return [];
  const groups: FeatureGroup[] = [];
  for (const child of root.children) {
    if (child.kind !== 'element') continue;
    if (child.tag === 'file') continue; // `<file name="..."/>` header in env/ files
    groups.push({
      id: child.id,
      docId: doc.id,
      tag: child.tag,
      label: groupLabel(child, ownerIndex(root, child)),
      color: getAttr(child, 'color'),
      count: child.children.filter((c) => c.kind === 'element' && isPointElement(c)).length,
    });
  }
  return groups;
}

/* ------------------------------------------------------------------ */
/* Mutations - all operate on a cloned document, callers keep it pure  */
/* ------------------------------------------------------------------ */

export function withDoc(doc: EditorDoc, mutate: (copy: XDocument) => void): EditorDoc {
  const copy = cloneDocumentPreservingIds(doc.doc);
  mutate(copy);
  return { ...doc, doc: copy };
}

export function findById(doc: XDocument, id: string): XElement | null {
  for (const { el } of walkElements(doc)) if (el.id === id) return el;
  return null;
}

export function findParent(doc: XDocument, id: string): XElement | null {
  for (const { el, parent } of walkElements(doc)) if (el.id === id) return parent;
  return null;
}

/** Defaults used when the editor creates a brand new point. */
export function defaultPointAttrs(kind: DocKind, x: number, z: number, radius: number): Record<string, string> {
  const fx = fmt(x);
  const fz = fmt(z);
  switch (kind) {
    case 'territories':
      return { name: 'Zone', smin: '1', smax: '2', dmin: '1', dmax: '3', x: fx, z: fz, r: fmt(radius) };
    case 'eventspawns':
      return { x: fx, z: fz, a: '0.0' };
    case 'playerspawns':
      return { x: fx, y: '0.0', z: fz };
    default:
      return { x: fx, z: fz };
  }
}

export function pointTagFor(kind: DocKind): string {
  return kind === 'territories' ? 'zone' : 'pos';
}

export function fmt(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(1) : String(rounded);
}

/** Creates the container a new group needs for the given file kind. */
export function newGroupElement(kind: DocKind, name: string): XElement {
  switch (kind) {
    case 'territories':
      return element('territory', { color: randomTerritoryColor() });
    case 'eventspawns':
      return element('event', { name });
    case 'playerspawns':
      return element('generator', { name });
    default:
      return element('group', { name });
  }
}

const TERRITORY_COLORS = ['0xFFE53935', '0xFF1E88E5', '0xFF43A047', '0xFFFB8C00', '0xFF8E24AA', '0xFF00ACC1'];

export function randomTerritoryColor(): string {
  return TERRITORY_COLORS[Math.floor(Math.random() * TERRITORY_COLORS.length)];
}

/** Turns a DayZ `0xAARRGGBB` colour into a CSS colour, falling back to the layer colour. */
export function dayzColorToCss(raw: string | undefined, fallback: string): string {
  if (!raw) return fallback;
  const hex = raw.trim().toLowerCase();
  const parsed = hex.startsWith('0x') ? Number.parseInt(hex.slice(2), 16) : Number.parseInt(hex, 10);
  if (!Number.isFinite(parsed)) return fallback;
  const r = (parsed >> 16) & 0xff;
  const g = (parsed >> 8) & 0xff;
  const b = parsed & 0xff;
  if (r === 0 && g === 0 && b === 0) return fallback;
  return `rgb(${r}, ${g}, ${b})`;
}

/* ------------------------------------------------------------------ */
/* events.xml / types.xml table rows                                   */
/* ------------------------------------------------------------------ */

export type EconomyRow = {
  id: string;
  name: string;
  fields: Record<string, string>;
  flags: Record<string, string>;
  /** types.xml usage / tier / category tags. */
  tags: { usage: string[]; value: string[]; category: string[]; tag: string[] };
};

export const EVENT_FIELDS = ['nominal', 'min', 'max', 'lifetime', 'restock', 'saferadius', 'distanceradius', 'cleanupradius', 'active'] as const;
export const TYPE_FIELDS = ['nominal', 'lifetime', 'restock', 'min', 'quantmin', 'quantmax', 'cost'] as const;

export function readEconomyRows(doc: EditorDoc): EconomyRow[] {
  const root = rootElement(doc.doc);
  if (!root) return [];
  const wanted = doc.kind === 'events' ? 'event' : 'type';
  const rows: EconomyRow[] = [];
  for (const child of root.children) {
    if (child.kind !== 'element' || child.tag !== wanted) continue;
    const fields: Record<string, string> = {};
    const flags: Record<string, string> = {};
    const tags = { usage: [] as string[], value: [] as string[], category: [] as string[], tag: [] as string[] };
    for (const sub of child.children) {
      if (sub.kind !== 'element') continue;
      if (sub.tag === 'flags') {
        for (const attr of sub.attrs) flags[attr.name] = attr.value;
      } else if (sub.tag === 'usage' || sub.tag === 'value' || sub.tag === 'category' || sub.tag === 'tag') {
        const name = getAttr(sub, 'name');
        if (name) tags[sub.tag].push(name);
      } else if (sub.children.length === 0 || sub.children.every((c) => c.kind === 'text')) {
        fields[sub.tag] = elementText(sub);
      }
    }
    rows.push({ id: child.id, name: getAttr(child, 'name') ?? '(unnamed)', fields, flags, tags });
  }
  return rows;
}

/** Writes a `<nominal>7</nominal>`-style value, creating the element if needed. */
export function setEconomyField(root: XElement, rowId: string, field: string, value: string): void {
  const row = root.children.find((c): c is XElement => c.kind === 'element' && c.id === rowId);
  if (!row) return;
  const existing = row.children.find((c): c is XElement => c.kind === 'element' && c.tag === field);
  if (existing) setElementText(existing, value);
  else row.children.push(textElement(field, value));
}

/** Adds or removes a `<usage name="Military"/>`-style tag. */
export type EconomyTag = 'usage' | 'value' | 'category' | 'tag';

export function toggleEconomyTag(root: XElement, rowId: string, tag: EconomyTag, name: string): void {
  const row = root.children.find((c): c is XElement => c.kind === 'element' && c.id === rowId);
  if (!row) return;
  const index = row.children.findIndex(
    (c) => c.kind === 'element' && c.tag === tag && getAttr(c, 'name') === name,
  );
  if (index >= 0) row.children.splice(index, 1);
  else row.children.push(element(tag, { name }));
}

export function setRowAttr(root: XElement, rowId: string, name: string, value: string): void {
  const row = root.children.find((c): c is XElement => c.kind === 'element' && c.id === rowId);
  if (row) setAttr(row, name, value);
}

export function newDocId(): string {
  return nextId('doc');
}
